/**
 * Belgian maltsters — Dingemans (Stabroek) and Castle Malting (Beloeil).
 *
 * Colour is published in EBC; the °L figure alongside it is the conversion
 * `°L = (EBC + 1.2) / 2.65` rounded to one decimal, not a second measurement.
 * Diastatic power is published in Windisch-Kolbach and converted with
 * `°Lintner = (°WK + 16) / 3.5`. Dingemans' `MD` suffix marks their
 * fully-modified ("mout de dégustation") grades. Entries marked
 * `representative` carry class-typical extract, moisture and enzyme figures
 * because those makers publish them only as broad ranges.
 *
 * ## Reading a Dingemans page
 *
 * Every product page prints a headline `Colour EBC` and, below it, a guarantee
 * band; the headline is what is stored, because it is the number Dingemans put
 * in the product's own name. Two malts disagree with themselves there —
 * Karrewheat 77 MD guarantees 70-100 EBC, whose mid-point is 85, and Cara MD
 * Pilsen heads 6 EBC against an analysis of 6.5 — and both rows keep the
 * headline and say so. The analysis also repeats `min. 230 WK` on nearly every
 * malt they sell, roasted and caramelised ones included, so that row is a
 * template rather than a measurement: it is read as a real figure only where
 * the malt plainly converts, and the Aroma grades, whose own prose calls their
 * enzyme activity low, carry class-typical numbers instead.
 *
 * Dingemans make no acidulated malt, no smoked or peated malt and no spelt, so
 * those gaps are theirs rather than ours. Their organic line is a full parallel
 * range with its own pages and its own analyses — the organic Belgian-origin
 * pilsner yields a point less than the European one, the organic biscuit malt
 * publishes an extract the conventional page withholds — and it is entered
 * product for product, the same way Castle's Nature line is.
 *
 * ## Reading a Castle Malting analysis
 *
 * Castle publish a Min/Max table on every product page in fifteen languages,
 * which makes them one of the best-documented maltsters anywhere. Colour is
 * the mid-point of the published band, or the single bound where they print
 * only a ceiling; extract is the published minimum and moisture the published
 * maximum, each stored as given. The `suggestedMaxPercent` figures are Castle's
 * own inclusion rates off the same pages, so this house's ceilings are the
 * maker's rather than ours — the one exception is Roasted Barley Nature, whose
 * page omits a rate, and which follows its conventional twin.
 *
 * Castle leave the diastatic row off every caramel and every roast, where zero
 * is what the process leaves rather than a figure being withheld; those rows
 * stay `spec-sheet`. Where the row is missing from a malt that plainly has
 * enzyme — the Vienna, the rye, the spelt, the emmer, the oat and the wheat
 * malts — the entry carries a class-typical number, says so, and is
 * `representative`. Château Vienna is the borderline case: its own organic
 * sibling publishes 250 WK, so that is the figure carried across.
 *
 * ## The Nature line
 *
 * `Nature` is Castle's certified-organic range and it mirrors most of the
 * conventional one, product for product, with its own analysis sheet and its
 * own small differences — the organic pilsen malt yields half a point more, the
 * organic pale ale malt guarantees 200 WK against 250. They are separate SKUs
 * with separate pages, so they are separate rows.
 *
 * ## What is not here
 *
 * Château Whisky, Whisky Light and Distilling, and their Nature siblings, are
 * sold on Predicted Spirit Yield with usage notes that name no beer at all;
 * they are distilling malts and are left out. Château Peated is not — at 5-10
 * ppm of phenol against the whisky malt's 30-45, and with a published brewing
 * inclusion rate, it is a brewing malt that happens to be smoked over peat. The
 * unmalted maize, oat and rice flakes are also out: Castle publish colour and
 * moisture for them and no extract at all, and the generic flake rows already
 * cover that ground.
 */

import type { Fermentable } from '../../schema/fermentable';

/** Dingemans and Castle Malting malts. @see Fermentable */
export const belgianFermentables: readonly Fermentable[] = [
  {
    id: 'castle-arome',
    name: 'Château Arôme',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 38.2,
    colorEbc: 100,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned to 115 C for aroma rather than color, and it gives amber and dark lagers a rounder malt character with a smoother bitterness than a colored malt of the same depth. Castle publish no diastatic figure for it, so the one here is class-typical against the aromatic malts beside it.',
    tags: ['aromatic', 'kilned', 'belgian', 'malty'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-AROME-English',
    provenance: 'representative',
  },
  {
    id: 'castle-cafe',
    name: 'Château Café',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 177.8,
    colorEbc: 470,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted to a genuine coffee note rather than to the ash a black malt gives, with a nutty edge behind it. It colors a stout or a porter and adds smoothness at the same time, which is why it goes in at 3-8 % rather than the 1-2 % a black malt wants.',
    tags: ['roasted', 'belgian', 'coffee', 'nutty', 'stout'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CAFE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-cara-blond',
    name: 'Château Cara Blond',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 8.5,
    colorLovibond: 8.2,
    colorEbc: 20.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step up from Cara Clair and still pale: mild caramel sweetness and a golden cast, with the glassy endosperm that holds a head. Castle allow it to 30 % of the grist, which is a lot for a caramel malt and reflects how little color it brings.',
    tags: ['crystal', 'caramel', 'belgian', 'golden', 'body'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-BLOND-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-cara-clair',
    name: 'Château Cara Clair',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 8.5,
    colorLovibond: 3.8,
    colorEbc: 9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest caramel Castle make, and sold as a dextrin malt as much as a caramel one: it builds body and head without moving the color. Soft biscuit on the nose, and at home in a pilsner or a low-alcohol beer where there is nothing else to carry the mouthfeel.',
    tags: ['crystal', 'caramel', 'belgian', 'dextrin', 'body'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-CLAIR-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-cara-gold',
    name: 'Château Cara Gold',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 45.7,
    colorEbc: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Germinated warm and then roasted to 220 C, which is what puts the toffee and brown sugar in it. A rich amber color and a glassy endosperm that holds foam; 5-15 % is plenty in a brown or dark Belgian beer.',
    tags: ['crystal', 'caramel', 'belgian', 'toffee', 'amber'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-GOLD-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-mroost-1400',
    name: 'Mroost 1400 MD (De-bittered Black)',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 528.8,
    colorEbc: 1400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Dingemans' de-bittered black: roasted hard and then chilled quickly, which is what keeps the astringency out of it. Coffee, cappuccino and cocoa nib without the scorched bitterness a black malt normally carries, so it colors a beer nearly as hard while arguing with it far less.",
    tags: ['roasted', 'belgian', 'debittered', 'de-bittered-black', 'black', 'coffee'],
    url: 'https://www.dingemansmout.be/malt/mroost-1400-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-munich-15-md',
    // 'Munich MD', not 'Munich 15 MD': Dingemans number their malts in EBC and
    // this package reads a number in a malt's name as Lovibond, which is true of
    // every other row that carries one. Their siblings here — Pilsen MD, Pale Ale
    // MD, Aromatic MD — carry no number either.
    name: 'Munich MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.5,
    moisturePercent: 4.5,
    colorLovibond: 6.1,
    colorEbc: 15,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 80,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Belgian Munich at 15 EBC, kilned for pronounced maltiness and an amber-orange cast, and enzymatic enough that Dingemans allow it to 80 % of the bill. Unlike their caramel and roasted rows its 230 WK is a real figure for a malt that genuinely converts.',
    tags: ['munich', 'kilned', 'belgian', 'malty', 'amber'],
    url: 'https://www.dingemansmout.be/malt/munich-15-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-pilsen-md',
    name: 'Pilsen MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Belgian reference pale base malt, fully modified and comfortable in a single-infusion mash. Slightly softer and grainier than a German pilsner malt, which is much of why Belgian pale ales and tripels taste the way they do.',
    tags: ['base', 'belgian', 'pilsner', 'lager'],
    url: 'https://www.dingemansmout.be/malt/pilsen-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-pale-ale-md',
    name: 'Pale Ale MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 73,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned a touch warmer and darker than Pilsen MD for a rounder, lightly bready base with ample enzyme. The usual foundation for Belgian blondes, saisons and abbey singles that want malt presence but no real color.',
    tags: ['base', 'belgian', 'ale', 'pale-ale'],
    url: 'https://www.dingemansmout.be/malt/pale-ale-9-md',
    provenance: 'spec-sheet',
  },
  // `dingemans-cara-45` and `dingemans-cara-120` are named for the EBC numbers
  // they were first entered under, and the ids stay that way on purpose: a
  // recipe that already stores one must keep resolving to the same malt. The
  // displayed names are the American ones, because that is what our brewers buy
  // — so both ids read one step darker than the name beside them. Dingemans
  // number in EBC, the US trade numbers in Lovibond, and the range maps
  // 20→Cara 8, 50→Cara 20, 120→Cara 45, 200→Cara 75. Each name carries both,
  // American number first and Dingemans' own in parentheses, for the reason
  // the Crisp crystals do: a brewer reading "Cara 120 MD" off a spec sheet who
  // finds a row called "Cara 45" concludes we do not stock it. The id is no
  // help — it is frozen, and it reads a step darker than the name beside it.
  {
    id: 'dingemans-cara-45',
    name: 'Cara 20 (Cara 50 MD)',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6.5,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle Dingemans caramel: strong caramel with a honeyed edge, dark enough to color an abbey beer or a Flemish brown and sweet enough to carry it. Dingemans call it Cara 50 MD after its EBC; the American trade calls it Cara 20 after its Lovibond. The entry previously carried 45 EBC, which is a number neither of them uses.',
    tags: ['belgian', 'crystal', 'caramel', 'cara-50-md', 'abbey', 'dubbel', 'honey'],
    url: 'https://www.dingemansmout.be/malt/cara-50-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-cara-120',
    name: 'Cara 45 (Cara 120 MD)',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 45.7,
    colorEbc: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Medium-dark caramel malt with rich toffee and raisin character and a deep amber cast. Standard seasoning at 3–8 % in dubbels, Belgian ambers and winter ales.',
    tags: ['crystal', 'caramel', 'belgian', 'dark-fruit', 'cara-120-md'],
    url: 'https://www.dingemansmout.be/malt/cara-120-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-aromatic-md',
    name: 'Aromatic MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 17,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A high-kilned malt sitting well past dark Munich in intensity, built to concentrate malt aroma rather than color. Retains a little enzyme but is used as a seasoning: 5–15 % gives a strong bready, almost sweet-bread character.',
    tags: ['kilned', 'belgian', 'aromatic', 'malty'],
    url: 'https://www.dingemansmout.be/malt/amber-aromatic%C2%AE-md',
    provenance: 'representative',
  },
  {
    id: 'dingemans-biscuit',
    name: 'Biscuit',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Lightly roasted rather than kilned, giving a distinct toasted-bread and digestive-biscuit note with no caramel sweetness. It carries no enzyme of its own, so mash it alongside a base malt at 3–10 %.',
    tags: ['kilned', 'toasted', 'belgian', 'biscuit'],
    url: 'https://www.dingemansmout.be/malt/biscuit%C2%AE-50%C2%A0md',
    provenance: 'representative',
  },
  {
    id: 'dingemans-special-b',
    name: 'Special B',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 113.7,
    colorEbc: 300,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The most distinctive Belgian specialty malt: an extremely dark caramel malt tasting of raisin, prune and burnt sugar with a faint leathery edge. Two to five per cent is usually enough to define a dubbel or quadrupel.',
    tags: ['crystal', 'caramel', 'belgian', 'dark-fruit', 'dubbel'],
    url: 'https://www.dingemansmout.be/malt/special-b%C2%AE-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-pilsen-2rs',
    name: 'Château Pilsen 2RS',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row spring barley pilsner malt and the highest-volume product in the Castle range. Pale, clean and well modified, with enough enzyme to convert a substantial fraction of unmalted adjunct alongside it.',
    tags: ['base', 'belgian', 'pilsner', 'lager', 'two-row'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-PILSEN-2RS-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-munich',
    name: 'Château Munich',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 9.7,
    colorEbc: 24.5,
    diastaticPowerLintner: 22,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Belgian-made Munich malt kilned for deep golden color and a full bread-crust flavor. Diastatic power is on the low side for the style, so blend with a pilsner base above about 50 % of the grist.',
    tags: ['kilned', 'belgian', 'munich', 'malty'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-MUNICH-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-cara-ruby',
    name: 'Château Cara Ruby',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 7,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle grade of the Castle caramel range, chosen for the red-amber hue it throws as much as for flavor. Contributes a clean caramel sweetness and improved body at 4–10 % in ambers, altbiers and Belgian reds.',
    tags: ['crystal', 'caramel', 'belgian', 'red'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-RUBY-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-wheat-blanc',
    name: 'Château Wheat Blanc',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 84.5,
    moisturePercent: 5.5,
    colorLovibond: 2.2,
    colorEbc: 4.5,
    diastaticPowerLintner: 67,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malted wheat with high extract and high protein, husk-free by nature. Builds the hazy body and lasting head of a witbier or Belgian pale; expect a slow runoff past about 40 % unless rice hulls go in.',
    tags: ['base', 'wheat', 'belgian', 'witbier', 'head-retention'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-BLANC-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-chocolat',
    name: 'Château Chocolat',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt roasted to chocolate color, giving cocoa and mild coffee notes with a dry, faintly bitter finish. Works at 2–6 % in dark Belgian ales, porters and stouts, or as a color correction in a dubbel.',
    tags: ['roasted', 'belgian', 'chocolate', 'stout'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CHOCOLAT-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-abbey',
    name: 'Château Abbey',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 17.4,
    colorEbc: 45,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A pale malt kilned on to 110 °C until it tastes of cooked bread, nuts and dried fruit. Castle sells it for abbey beers and dark Belgians, up to a quarter of the grist; it carries a bitterness when young that softens as the beer ages. Sold as Château Monastique in the United States.',
    tags: ['belgian', 'abbey', 'kilned', 'toasted', 'bready', 'dubbel'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-ABBEY-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-biscuit',
    name: 'Château Biscuit',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted just far enough to taste of the crust and not the crumb: warm biscuit and toasted bread, with none of the coffee a darker malt brings. A few percent deepens a dubbel or a brown ale; Castle allows a quarter of the grist.',
    tags: ['belgian', 'biscuit', 'toasted', 'brown-ale', 'dubbel', 'bready'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-BISCUIT-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-special-belgium',
    name: 'Château Special Belgium',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 109.4,
    colorEbc: 290,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Castle\u2019s answer to Special B, and the same job: raisin, prune and dark toffee concentrated hard enough that ten percent is the ceiling. It is what makes a dubbel taste of dried fruit rather than of caramel.',
    tags: ['belgian', 'crystal', 'caramel', 'dark', 'raisin', 'dubbel', 'quadrupel'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-SPECIAL-BELGIUM-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-black',
    name: 'Château Black',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 506.9,
    colorEbc: 1350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Black malt in the plain sense: roasted to the far end for color and a sharp, dry, faintly burnt bitterness. Used in small fractions for the last shade of a stout or a dark Belgian, where a debittered malt would give color and say nothing.',
    tags: ['belgian', 'roasted', 'black', 'stout', 'dry', 'colour'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-BLACK-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-cara-8',
    name: 'Cara 8 (Cara 20 MD)',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 9.5,
    colorLovibond: 8,
    colorEbc: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lightest of the Dingemans caramels, drum-roasted at low temperature so it adds body and head retention without color. Five percent visibly improves the head on a pils; Dingemans will take it to thirty in a blonde or a tripel that wants depth rather than darkness. Dingemans themselves call it Cara 20 MD: they number their caramel malts in EBC and the American trade renumbers them in Lovibond, and 20 EBC is 8 °L.',
    tags: [
      'belgian',
      'crystal',
      'caramel',
      'light',
      'head-retention',
      'blonde',
      'tripel',
      'cara-8',
    ],
    url: 'https://www.dingemansmout.be/malt/cara-20-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-wheat-malt-md',
    name: 'Wheat Malt MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 6.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Belgian wheat malt at 83 % extract, enzyme-rich enough to be most of a grist, and the haze and head behind a witbier. Husk-free like every wheat malt, so plan the lauter above about half the bill.',
    tags: ['base', 'wheat', 'belgian', 'witbier', 'head-retention'],
    url: 'https://www.dingemansmout.be/malt/wheat-malt-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-pale-ale',
    name: 'Château Pale Ale',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 3.7,
    colorEbc: 8.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned a step past the pilsen malt to 7-10 EBC, which is where a Belgian base malt starts tasting of biscuit rather than of grain. At 250 WK it converts a whole grist, and Castle sell it as a 100 % malt for bitters and English-leaning ales.',
    tags: ['base', 'ale', 'belgian', 'pale-ale', 'biscuit'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-PALE-ALE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-pilsen-6rw',
    name: 'Château Pilsen 6RW',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Six-row winter barley rather than the two-row spring barley of Pilsen 2RS: two points less extract, more husk and more protein, but 300 WK of enzyme against 250. Castle blend it up to half the grist with the two-row malt, which is exactly what six-row is for — converting adjuncts.',
    tags: ['base', 'pilsner', 'lager', 'belgian', 'six-row', 'high-enzyme'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-PILSEN-6RW-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-diastatic',
    name: 'Château Diastatic',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 1.7,
    colorEbc: 3.25,
    diastaticPowerLintner: 104.6,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A pale malt kilned to preserve amylase rather than to build flavor: 350 WK, half again what their pilsen malt guarantees. Its job is to drag a grist heavy in unmalted grain or low-enzyme specialty malt over the line, at up to 30 % of the bill.',
    tags: ['base', 'high-enzyme', 'belgian', 'adjunct-conversion', 'pale'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-DIASTATIC-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-vienna',
    name: 'Château Vienna',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Amber-kilned at 4-7 EBC, which is pale for a Vienna and shows how gently Castle take it — golden color and a light toasted sweetness with no caramel behind it. Their organic Vienna publishes 250 WK and this page publishes none, so that figure is carried across; on it, the malt converts a whole grist.',
    tags: ['kilned', 'vienna', 'belgian', 'golden', 'malty'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-VIENNA-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-munich-light',
    name: 'Château Munich Light',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 6.1,
    colorEbc: 15,
    diastaticPowerLintner: 47.4,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The paler of the two Castle Munichs at 12-18 EBC, and the only one of the pair with a published enzyme figure: 150 WK, enough to carry itself. Bread crust and malt depth up to 60 % of the mix, where the darker grade needs help.',
    tags: ['kilned', 'munich', 'belgian', 'malty', 'bready'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-MUNICH-LIGHT-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-red-intense',
    name: 'Château Red Intense',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 14,
    colorEbc: 36,
    diastaticPowerLintner: 61.7,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A red malt that still assays 200 WK at 34-38 EBC, which is unusual: it can be the whole grist and convert what sits beside it. Castle built it for red ales and red lagers, so the color arrives without a dark crystal being dragged in to supply it.',
    tags: ['kilned', 'red', 'belgian', 'malty', 'high-enzyme'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-RED-INTENSE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-melano',
    name: 'Château Melano',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 30.6,
    colorEbc: 80,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Melanoidin malt made on a long, wet, hot cure so the Maillard chemistry runs in the kiln rather than in the kettle. Ruby color and a frank malt sweetness at 75-85 EBC; Castle stop at a fifth of the grist and it is loud well before that.',
    tags: ['kilned', 'melanoidin', 'belgian', 'red', 'malty'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-MELANO-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-melano-light',
    name: 'Château Melano Light',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 15.5,
    colorEbc: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Half the color of the full Melano at 36-44 EBC and the same job at a lower dose of color: bready, faintly honeyed malt depth for a Scottish or amber ale that must not go red. Same 20 % ceiling.',
    tags: ['kilned', 'melanoidin', 'belgian', 'amber', 'malty'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-MELANO-LIGHT-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-peated',
    name: 'Château Peated',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt cured over Scottish peat to a restrained 5-10 ppm of phenol — a tenth of what a whisky malt carries — and kept fully enzymatic at 250 WK. This is the brewing grade: Castle put it at up to 10 % in Scottish ales, porters and smoked beers, and peat asserts itself long before the ceiling.',
    tags: ['smoked', 'peated', 'belgian', 'phenolic', 'scottish-ale'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-PEATED-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-smoked',
    name: 'Château Smoked',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 3.3,
    colorEbc: 7.45,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Beechwood smoke rather than peat, which reads savory and sweet where peat reads medicinal, at 3-15 ppm of phenol. Still 250 WK, so the smoke is the only thing it changes; Castle allow it to 15 % for rauchbier and Scottish ales.',
    tags: ['smoked', 'beechwood', 'belgian', 'rauchbier', 'phenolic'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-SMOKED-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-wheat-smoked',
    name: 'Château Wheat Smoked',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5.5,
    colorLovibond: 3.5,
    colorEbc: 8,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Smoked wheat malt at 10-15 ppm of phenol, which is the grist a Polish Grätzer is made of — pale, tart and smoky. Husk-free, so plan the runoff. Castle publish no diastatic figure for it, so that one is class-typical for wheat malt.',
    tags: ['smoked', 'wheat', 'belgian', 'grodziskie', 'phenolic'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-SMOKED-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-rye-malt',
    name: 'Château Rye Malt',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 6,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Belgian malted rye at 81 % extract and 3-8 EBC, bringing pepper, bread and a slick body. Castle take it to 30 % of the mix; it is huskless, so the lauter needs planning above about a fifth. No diastatic power is published, so that figure is class-typical for malted rye.',
    tags: ['rye', 'base', 'belgian', 'spicy', 'bread'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-RYE-MALT-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-spelt-malt',
    name: 'Château Spelt Malt',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 79.5,
    moisturePercent: 6,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Spelt is the hulled ancestor of modern wheat, and malted it gives a nutty, slightly honeyed grain note and a softer body than wheat does. Protein runs to 17 %, which is why Castle keep it under 15 % of a saison or a wheat beer. No diastatic figure is published, so that one is class-typical.',
    tags: ['spelt', 'base', 'belgian', 'ancient-grain', 'saison'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-SPELT-MALT-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-emmer-malt',
    name: 'Château Emmer Malt',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5.5,
    colorLovibond: 1.9,
    colorEbc: 3.75,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Emmer is older still than spelt, and Castle malt it to 81 % extract at 2.5-5 EBC for Emmerbier and for the reconstructed ancient styles brewers keep going back to. Usable to 40 % as a wheat substitute. Protein is published as a wide 10-20 % band and no diastatic power at all, so that figure is class-typical.',
    tags: ['emmer', 'base', 'belgian', 'ancient-grain', 'nutty'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-EMMER-MALT-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-oat-malt',
    name: 'Château Oat Malt',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 78.5,
    moisturePercent: 7,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted oats rather than flaked, and at 78.5 % an unusually high extract for the grain. Silky body and a creamy mouthfeel in a stout or a robust British ale, at up to 15 %. Protein reaches 17 % and no diastatic power is published, so that figure is class-typical for oat malt.',
    tags: ['oat', 'adjunct', 'belgian', 'body', 'creamy'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-OAT-MALT-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-buckwheat-malt',
    name: 'Château Buckwheat Malt',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 70,
    moisturePercent: 8,
    colorLovibond: 2.9,
    colorEbc: 6.5,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Buckwheat is not a cereal at all but a knotweed, and malted it gives an earthy, faintly bitter grain character nothing else provides. Castle allow it to 40 % of a specialty grist. They publish color, moisture and protein but no extract and no diastatic power, so both of those are class-typical here — and it is malted in a barley malthouse, so it is not a gluten-free ingredient.',
    tags: ['buckwheat', 'adjunct', 'belgian', 'pseudo-cereal', 'specialty'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-BUCKWHEAT-MALT-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-acid',
    name: 'Château Acid',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'acidulated',
    extractFineDryPercent: 76,
    moisturePercent: 10,
    colorLovibond: 4.6,
    colorEbc: 11,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt soured on its own lactobacilli, which is how a brewer drops mash pH without touching an acid bottle. Castle publish a wort pH ceiling of 5.0 and no extract at all, so the extract here is class-typical for an acid malt; a percent or two moves the mash, and beyond about ten the beer starts to taste of it.',
    tags: ['acidulated', 'belgian', 'ph', 'sour', 'mash-ph'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-ACID-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-wheat-arome',
    name: 'Château Wheat Arôme',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 34.4,
    colorEbc: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat malt kilned to 80-100 EBC, so it brings the aroma of a dark Munich with the protein and foam of a wheat. It is what puts the bread crust into a dunkelweizen or a weizenbock, at up to a quarter of the grist.',
    tags: ['wheat', 'aromatic', 'belgian', 'dunkelweizen', 'malty'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-AROME-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-wheat-munich',
    name: 'Château Wheat Munich',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 5,
    colorLovibond: 9.7,
    colorEbc: 24.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Munich-kilned wheat at 21-28 EBC and 83 % extract — the highest yield in the Castle range, because there is no husk in it. Body, foam and a bready depth in dark wheat beers at up to 30 %. Castle publish no diastatic figure, so it is treated as spent and the grist should convert it.',
    tags: ['wheat', 'munich', 'belgian', 'weizenbock', 'head-retention'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-MUNICH-25-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-wheat-munich-light',
    name: 'Château Wheat Munich Light',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 5,
    colorLovibond: 6.5,
    colorEbc: 16,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The paler wheat Munich at 14-18 EBC, for weizenbocks and dark wheat beers that want the body and the head without the color. Castle publish no diastatic figure, so it is treated as spent here.',
    tags: ['wheat', 'munich', 'belgian', 'light', 'head-retention'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-MUNICH-LIGHT-16-EBC-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-cara-arome',
    name: 'Château Cara Arôme',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6.5,
    colorLovibond: 34.4,
    colorEbc: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A caramel malt stewed for aroma as much as for color, landing at 80-100 EBC with a deep toffee nose. Castle put it at up to 15 % in any lager or ale, where it does the work of a darker crystal at a lighter color.',
    tags: ['crystal', 'caramel', 'belgian', 'aromatic', 'amber'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-AROME-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-belga',
    name: 'Château Cara Belgium',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 8,
    colorLovibond: 12.7,
    colorEbc: 32.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A pale caramel at 30-35 EBC built for the Belgian styles specifically — blonde, bruin, dubbel, tripel — where sweetness and body are wanted and color is not. Castle allow it to 30 % of the mash bill.',
    tags: ['crystal', 'caramel', 'belgian', 'blonde', 'dubbel'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-BELGA-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-cafe',
    name: 'Château Cara Café',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 151.4,
    colorEbc: 400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Stewed like a crystal and then taken to 350-450 EBC, which is where caramel starts reading as coffee. It carries the sweetness of a caramel malt and the color of a roast, and Castle allow it to 15 % in stouts, bocks and dark ales.',
    tags: ['crystal', 'caramel', 'belgian', 'coffee', 'dark'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-CAFE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-crystal',
    name: 'Château Cara Crystal',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The straight middle-of-the-road caramel at 140-160 EBC: toffee, a full body and an amber-to-copper cast. Up to 15 % in a brown ale, a dark lager or a bock, though five or six is usually the point where it stops improving things.',
    tags: ['crystal', 'caramel', 'belgian', 'toffee', 'amber'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-CRYSTAL-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-honey',
    name: 'Château Cara Honey',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6.5,
    colorLovibond: 26.9,
    colorEbc: 70,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Stewed to 60-80 EBC and named for the honeyed sweetness it leaves rather than for its color. Red-amber and rounder than a plain caramel of the same depth; Castle take it to 20 % of the mash bill in ambers, bocks and dunkels.',
    tags: ['crystal', 'caramel', 'belgian', 'honey', 'amber'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-HONEY-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-terra',
    name: 'Château Cara Terra',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 74,
    colorEbc: 195,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A dark caramel at 170-220 EBC pitched at Bohemian lagers and porters rather than at Belgian ales: earthier and drier than Special Belgium at a similar depth, with dried fruit rather than raisin. Fifteen percent is the ceiling and it is emphatic at five.',
    tags: ['crystal', 'caramel', 'belgian', 'dark', 'earthy', 'porter'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-TERRA-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-ultra-150',
    name: 'Château Cara Ultra 150',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The same 140-160 EBC as Cara Crystal, stewed harder for a more concentrated caramel: Castle sell the Ultra grades on intensity at a given color rather than on color itself. One to twenty percent in ambers, browns and dark lagers.',
    tags: ['crystal', 'caramel', 'belgian', 'intense', 'amber'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-ULTRA-150-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-ultra-60',
    name: 'Château Cara Ultra 60',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 8,
    colorLovibond: 23.1,
    colorEbc: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The paler Ultra at 50-70 EBC, and the one to reach for when a light lager or an IPA needs body and caramel without turning amber. Castle allow it to 30 %, which is generous for a caramel malt and reflects how little color it moves.',
    tags: ['crystal', 'caramel', 'belgian', 'intense', 'golden'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-ULTRA-60-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-crystal',
    name: 'Château Crystal',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A crystal made the drier way — 4.5 % moisture against the 6 % of the Cara range, and 78 % extract, the highest of any Castle caramel. Same 140-160 EBC as Cara Crystal, with a cleaner, glassier caramel and better foam. Up to 20 %.',
    tags: ['crystal', 'caramel', 'belgian', 'toffee', 'high-extract'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CRYSTAL-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-wheat-crystal',
    name: 'Château Wheat Crystal',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat stewed into a crystal at 140-160 EBC, which gives the caramel of one and the foam of the other. Castle put it at up to 20 % in dunkelweizen and weizenbock, where a barley crystal would flatten the head it is meant to build.',
    tags: ['crystal', 'caramel', 'wheat', 'belgian', 'head-retention'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-CRYSTAL-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cafe-light',
    name: 'Château Café Light',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 94.8,
    colorEbc: 250,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The pale end of the Café range at 220-280 EBC: fresh-roasted coffee without the depth or the bitterness of the full grade. A percent or two lifts a brown ale; Castle stop at ten.',
    tags: ['roasted', 'belgian', 'coffee', 'brown-ale', 'light-roast'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CAFE-LIGHT-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-chocolat-light',
    name: 'Château Chocolat Light',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 226.9,
    colorEbc: 600,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 7,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted to 550-650 EBC rather than the 800-1000 of the full Chocolat, which keeps the cocoa and drops most of the char. It also holds six points more extract. Two to seven percent in a porter or a brown ale.',
    tags: ['roasted', 'chocolate', 'belgian', 'cocoa', 'porter'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CHOCOLAT-LIGHT-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-roasted-barley',
    name: 'Château Roasted Barley',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 3,
    colorLovibond: 453.3,
    colorEbc: 1200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley roasted past 1200 EBC — Castle publish that only as a floor — and drier, sharper and more astringent than a black malt for it. The espresso bite of an Irish stout: 2-4 % in a brown ale, 3-10 % in a porter or stout.',
    tags: ['roasted', 'unmalted', 'belgian', 'stout', 'coffee', 'dry'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-ROASTED-BARLEY-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-wheat-black',
    name: 'Château Wheat Black',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 491,
    colorEbc: 1300,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat roasted to 1200-1400 EBC, which colors as hard as a black malt and argues far less because there is no husk in it to turn acrid. At 78 % it also keeps eight points more extract than the barley black. Up to 20 % in a schwarzbier or a black IPA.',
    tags: ['roasted', 'wheat', 'belgian', 'black', 'smooth', 'schwarzbier'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-BLACK-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-wheat-cafe',
    name: 'Château Wheat Café',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 77,
    moisturePercent: 5.5,
    colorLovibond: 151.4,
    colorEbc: 400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted wheat at 350-450 EBC, sitting where a coffee malt sits but without the husk bitterness. Color and a mild roast note for a bock, an altbier or a brown ale, at up to a fifth of the grist.',
    tags: ['roasted', 'wheat', 'belgian', 'coffee', 'altbier', 'smooth'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-CAFE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-wheat-chocolat',
    name: 'Château Wheat Chocolat',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 358.9,
    colorEbc: 950,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat roasted to chocolate depth at 800-1100 EBC, smoother than the barley grade and holding 78 % extract where that one holds 70. It darkens an English stout or a porter without roughening it, and Castle allow a generous 20 %.',
    tags: ['roasted', 'wheat', 'belgian', 'chocolate', 'smooth', 'stout'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-CHOCOLAT-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-chit-barley-flakes',
    name: 'Château Chit Barley Malt Flakes',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 11,
    colorLovibond: 2.2,
    colorEbc: 4.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Chit malt — barley stopped the moment it germinates — steamed and rolled into flakes, so it goes in without milling. The high-molecular protein a full modification would break down survives, which is what firms up the foam and the body. Up to 30 % of the mash bill; the steaming leaves it no enzyme of its own.',
    tags: ['chit', 'flaked', 'barley', 'belgian', 'foam', 'body'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CHIT-BARLEY-MALT-FLAKES-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-chit-wheat-flakes',
    name: 'Château Chit Wheat Malt Flakes',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 2.2,
    colorEbc: 4.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The wheat version of the chit flake, and a shorter route to the dense head and hazy body of a witbier than milling raw wheat. Five points drier than the barley flake as shipped, and used the same way at up to 25 % of the mix.',
    tags: ['chit', 'flaked', 'wheat', 'belgian', 'foam', 'body'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CHIT-WHEAT-MALT-FLAKES-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-pilsen-nature',
    name: 'Château Pilsen Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81.5,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    colorEbc: 3.25,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The certified-organic pilsen malt, and on paper the better of the pair: half a point more extract than Pilsen 2RS and a color band half as wide, at 3-3.5 EBC. Same 250 WK, same whole-grist use for pale organic lagers and ales.',
    tags: ['base', 'pilsner', 'lager', 'belgian', 'organic'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-PILSEN-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-pale-ale-nature',
    name: 'Château Pale Ale Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 3.7,
    colorEbc: 8.5,
    diastaticPowerLintner: 61.7,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic pale ale malt at 7-10 EBC, matching the conventional grade for extract and color and guaranteeing 200 WK where that one guarantees 250 — still ample for a full grist. The biscuit base under an organic bitter.',
    tags: ['base', 'ale', 'belgian', 'pale-ale', 'organic'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-PALE-ALE-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-vienna-nature',
    name: 'Château Vienna Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic Vienna at 4-7 EBC with a published 250 WK — the enzyme figure the conventional page leaves off. Golden color and a gentle toast, usable as the entire grist of an organic Vienna lager.',
    tags: ['kilned', 'vienna', 'belgian', 'organic', 'golden'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-VIENNA-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-munich-nature',
    name: 'Château Munich Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 9.7,
    colorEbc: 24.5,
    diastaticPowerLintner: 22,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic Munich at 21-28 EBC, deep golden and frankly bready, and allowed to 60 % of the mix. Castle publish no diastatic power for it any more than for its conventional twin, so the figure here is the class-typical one that row carries: blend a pilsner base underneath it.',
    tags: ['kilned', 'munich', 'belgian', 'organic', 'malty'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-MUNICH-NATURE-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-munich-light-nature',
    name: 'Château Munich Light Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 6.1,
    colorEbc: 15,
    diastaticPowerLintner: 47.4,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic light Munich, 12-18 EBC and 150 WK — enzymatic enough to stand alone, which the darker organic grade is not. Up to 60 % of an organic bock or dunkel.',
    tags: ['kilned', 'munich', 'belgian', 'organic', 'bready'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-MUNICH-LIGHT-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-abbey-nature',
    name: 'Château Abbey Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 17.4,
    colorEbc: 45,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic Abbey malt, kilned to the same 41-49 EBC as its conventional twin: cooked bread, nuts and dried fruit, for organic abbey beers and dark Belgians at up to a quarter of the grist. Sold as Château Monastique Nature in the United States.',
    tags: ['belgian', 'abbey', 'kilned', 'toasted', 'organic', 'dubbel'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-ABBEY-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-biscuit-nature',
    name: 'Château Biscuit Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic biscuit malt at 45-55 EBC, roasted just far enough for crust and warm biscuit and no further. It carries no enzyme, so mash it against a base malt; Castle allow a quarter of the bill.',
    tags: ['belgian', 'biscuit', 'toasted', 'organic', 'bready'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-BISCUIT-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-arome-nature',
    name: 'Château Arôme Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 38.2,
    colorEbc: 100,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic aromatic malt kilned hard to 95-105 EBC, for the roundest malt character Castle can build without a caramel. Up to 20 % of an organic amber or dark lager. No diastatic figure is published, so the one here is the class-typical figure its conventional twin already carries.',
    tags: ['aromatic', 'kilned', 'belgian', 'organic', 'malty'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-AROME-NATURE-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-melano-nature',
    name: 'Château Melano Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 30.6,
    colorEbc: 80,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic melanoidin malt at 75-85 EBC — ruby color and a heavy malt sweetness built in the kiln rather than the kettle. A fifth of the grist is the ceiling for organic Scottish, amber and red ales.',
    tags: ['kilned', 'melanoidin', 'belgian', 'organic', 'red'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-MELANO-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-melano-light-nature',
    name: 'Château Melano Light Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 15.5,
    colorEbc: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic Melano at half the color, 36-44 EBC, for the same bready depth without the red. Same 20 % ceiling and the same absence of enzyme.',
    tags: ['kilned', 'melanoidin', 'belgian', 'organic', 'amber'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-MELANO-LIGHT-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-peated-nature',
    name: 'Château Peated Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 5.5,
    colorLovibond: 2.1,
    colorEbc: 4.25,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic peated malt, 5-10 ppm of phenol over a fully enzymatic 250 WK pale base. Castle recommend up to 10 % in organic Scottish ales, porters and smoked beers; a couple of percent already reads as peat.',
    tags: ['smoked', 'peated', 'belgian', 'organic', 'phenolic'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-PEATED-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-smoked-nature',
    name: 'Château Smoked Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 3.3,
    colorEbc: 7.45,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic beechwood-smoked malt at 3-15 ppm of phenol and 250 WK, matching the conventional grade point for point. Up to 15 % for an organic rauchbier or Scottish ale.',
    tags: ['smoked', 'beechwood', 'belgian', 'organic', 'rauchbier'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-SMOKED-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-wheat-blanc-nature',
    name: 'Château Wheat Blanc Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 5.5,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 67,
    suggestedMaxPercent: 35,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic pale wheat malt at 83 % extract and under 5 EBC, for the haze and the head of an organic witbier at up to 35 % of the mix. Husk-free, so plan the lauter. Castle publish no diastatic power, so that figure is class-typical for wheat malt.',
    tags: ['base', 'wheat', 'belgian', 'organic', 'witbier', 'head-retention'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-BLANC-NATURE-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-wheat-arome-nature',
    name: 'Château Wheat Arôme Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 34.4,
    colorEbc: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic wheat malt kilned to 80-100 EBC: the aroma of a dark Munich carried on wheat protein and wheat foam. Up to a quarter of an organic dunkelweizen or weizenbock.',
    tags: ['wheat', 'aromatic', 'belgian', 'organic', 'dunkelweizen'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-AROME-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-acid-nature',
    name: 'Château Acid Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'acidulated',
    extractFineDryPercent: 76,
    moisturePercent: 10,
    colorLovibond: 4.6,
    colorEbc: 11,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic acid malt, soured on its own lactobacilli and used a percent or two at a time to pull mash pH down in carbonate water. Castle publish color, moisture and protein but no extract, so that figure is class-typical for an acid malt.',
    tags: ['acidulated', 'belgian', 'organic', 'ph', 'mash-ph'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-ACID-NATURE-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-buckwheat-nature-malt',
    name: 'Château Buckwheat Nature Malt',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 70,
    moisturePercent: 8,
    colorLovibond: 2.9,
    colorEbc: 6.5,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Certified-organic malted buckwheat, earthy and faintly bitter, and allowed to 40 % of a specialty grist. As with the conventional grade Castle publish no extract and no diastatic power, so both are class-typical — and it is malted alongside barley, so it is not a gluten-free ingredient.',
    tags: ['buckwheat', 'adjunct', 'belgian', 'organic', 'pseudo-cereal'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-BUCKWHEAT-NATURE-MALT-English',
    provenance: 'representative',
  },
  {
    id: 'castle-chateau-cara-clair-nature',
    name: 'Château Cara Clair Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 8.5,
    colorLovibond: 3.8,
    colorEbc: 9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest organic caramel, capped at 9 EBC and sold as a dextrin malt as much as a caramel one: body and foam with no color. Up to 30 % of an organic pilsner or a low-alcohol beer.',
    tags: ['crystal', 'caramel', 'belgian', 'organic', 'dextrin', 'body'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-CLAIR-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-blond-nature',
    name: 'Château Cara Blond Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 8.5,
    colorLovibond: 8.2,
    colorEbc: 20.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step up from Cara Clair at 17-24 EBC and still pale: mild caramel and a golden cast for organic light lagers, light ales and white beers, at up to 30 % of the mix.',
    tags: ['crystal', 'caramel', 'belgian', 'organic', 'golden'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-BLOND-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-ruby-nature',
    name: 'Château Cara Ruby Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 7,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic middle caramel at 45-55 EBC, bought for the red-amber hue as much as the flavor. Clean caramel sweetness and more body in organic brown ales, bocks and Scottish ales; up to a quarter of the mix.',
    tags: ['crystal', 'caramel', 'belgian', 'organic', 'red'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-RUBY-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-gold-nature',
    name: 'Château Cara Gold Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 45.7,
    colorEbc: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic Cara Gold at 110-130 EBC: toffee and brown sugar over a rich amber, with the glassy endosperm that holds a head. Up to 20 % in organic brown and dark beers.',
    tags: ['crystal', 'caramel', 'belgian', 'organic', 'toffee', 'amber'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-GOLD-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-honey-nature',
    name: 'Château Cara Honey Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6.5,
    colorLovibond: 26.9,
    colorEbc: 70,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic caramel at 60-80 EBC named for its honeyed sweetness rather than its color, and rounder than a plain caramel of the same depth. Up to 20 % in organic ambers, bocks and dunkels.',
    tags: ['crystal', 'caramel', 'belgian', 'organic', 'honey'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-HONEY-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-arome-nature',
    name: 'Château Cara Arôme Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6.5,
    colorLovibond: 34.4,
    colorEbc: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic aromatic caramel at 80-100 EBC, stewed for a deep toffee nose rather than for color. Up to 15 % of any organic lager or ale.',
    tags: ['crystal', 'caramel', 'belgian', 'organic', 'aromatic'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-AROME-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-crystal-nature',
    name: 'Château Cara Crystal Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic mid-range caramel at 140-160 EBC: toffee, body and a copper cast for organic brown ales, dark lagers and bocks. Fifteen percent is the ceiling.',
    tags: ['crystal', 'caramel', 'belgian', 'organic', 'toffee'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-CRYSTAL-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-crystal-nature',
    name: 'Château Crystal Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The dry-stewed organic crystal: 4.5 % moisture and 78 % extract at the same 140-160 EBC as Cara Crystal Nature, with a cleaner caramel and firmer foam for it. Up to 20 %.',
    tags: ['crystal', 'caramel', 'belgian', 'organic', 'high-extract'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CRYSTAL-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-cafe-nature',
    name: 'Château Cara Café Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 151.4,
    colorEbc: 400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic caramel taken to 350-450 EBC, where the sweetness of a crystal meets the color of a roast. Up to 15 % in organic stouts, bocks, dark ales and barley wines.',
    tags: ['crystal', 'caramel', 'belgian', 'organic', 'coffee', 'dark'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-CAFE-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-special-belgium-nature',
    name: 'Château Special Belgium Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 109.9,
    colorEbc: 290,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic Special Belgium at 260-320 EBC, and the reason an organic dubbel can taste of dried fruit rather than of caramel: raisin, prune and dark toffee concentrated hard enough that ten percent is the ceiling.',
    tags: ['belgian', 'crystal', 'caramel', 'organic', 'raisin', 'dubbel'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-SPECIAL-BELGIUM-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-wheat-blond-nature',
    name: 'Château Cara Wheat Blond Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 68,
    moisturePercent: 8,
    colorLovibond: 6.1,
    colorEbc: 15,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic wheat stewed into a pale caramel at 10-20 EBC — body, foam and a light sweetness for organic wheat beers and low-alcohol beers. Extract is only 68 %, which is the price of stewing a huskless grain this gently; up to 30 % of the mix.',
    tags: ['crystal', 'caramel', 'wheat', 'belgian', 'organic', 'head-retention'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-WHEAT-BLOND-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cara-wheat-ruby-nature',
    name: 'Château Cara Wheat Ruby Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 68,
    moisturePercent: 8,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The red grade of the organic wheat caramel at 40-60 EBC, for organic witbier and red ale that need color and body without losing the head. Same modest 68 % extract as its blond sibling, and the same 30 % ceiling.',
    tags: ['crystal', 'caramel', 'wheat', 'belgian', 'organic', 'red'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CARA-WHEAT-RUBY-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-wheat-crystal-nature',
    name: 'Château Wheat Crystal Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 58,
    colorEbc: 152.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic wheat crystal at 140-165 EBC and 78 % extract, ten points above the Cara Wheat grades because it is stewed dry rather than moist. Caramel and foam together in an organic dunkelweizen, at up to 20 %.',
    tags: ['crystal', 'caramel', 'wheat', 'belgian', 'organic', 'weizenbock'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-WHEAT-CRYSTAL-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cafe-nature',
    name: 'Château Café Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 177.8,
    colorEbc: 470,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic coffee malt at 420-520 EBC, roasted to a genuine coffee note rather than to ash, with a nutty edge behind it. It colors and smooths an organic stout at the same time, which is why it goes in at 3-8 % rather than the one or two a black malt wants.',
    tags: ['roasted', 'belgian', 'organic', 'coffee', 'nutty', 'stout'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CAFE-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-cafe-light-nature',
    name: 'Château Café Light Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 94.8,
    colorEbc: 250,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The pale organic Café at 220-280 EBC — fresh-roasted coffee without the depth or the bite. A percent or two lifts an organic brown ale; ten is the ceiling.',
    tags: ['roasted', 'belgian', 'organic', 'coffee', 'brown-ale'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CAFE-LIGHT-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-chocolat-nature',
    name: 'Château Chocolat Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 7,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic pale malt roasted to 800-1000 EBC for cocoa and mild coffee with a dry, faintly bitter finish. Two to seven percent in organic porters, stouts and dark Belgians.',
    tags: ['roasted', 'chocolate', 'belgian', 'organic', 'cocoa'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CHOCOLAT-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-black-nature',
    name: 'Château Black Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 491,
    colorEbc: 1300,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic black malt at 1200-1400 EBC, roasted to the far end for color and a sharp, dry, burnt bitterness. Castle put it at 3-6 % of an organic stout or porter, and it is doing most of the coloring by the bottom of that band.',
    tags: ['belgian', 'roasted', 'black', 'organic', 'colour', 'dry'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-BLACK-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-roasted-barley-nature',
    name: 'Château Roasted Barley Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4.5,
    colorLovibond: 453.3,
    colorEbc: 1200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted organic barley roasted to 1000-1400 EBC — drier, sharper and more astringent than a black malt, and the dry espresso finish of an organic Irish stout. Castle publish no inclusion rate for this one, so the ten percent ceiling here is ours and follows their conventional grade.',
    tags: ['roasted', 'unmalted', 'belgian', 'organic', 'stout', 'coffee'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-ROASTED-BARLEY-NATURE-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-chit-barley-flakes-nature',
    name: 'Château Chit Barley Nature Malt Flakes',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 11,
    colorLovibond: 2.2,
    colorEbc: 4.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic chit malt steamed and rolled, so it goes into the mash without milling and brings the protein a fully modified malt has already spent. Foam and body at up to 30 % of the bill; the steaming leaves it no enzyme.',
    tags: ['chit', 'flaked', 'barley', 'belgian', 'organic', 'foam'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CHIT-BARLEY-NATURE-MALT-FLAKES-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-chateau-chit-wheat-flakes-nature',
    name: 'Château Chit Wheat Nature Malt Flakes',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 2.2,
    colorEbc: 4.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic wheat chit flake — the shortest route to the dense head and soft haze of an organic witbier, at up to 25 % of the mix and with no milling required.',
    tags: ['chit', 'flaked', 'wheat', 'belgian', 'organic', 'foam'],
    url: 'https://www.castlemalting.com/MaltInfo/?CHATEAU-CHIT-WHEAT-NATURE-MALT-FLAKES-English',
    provenance: 'spec-sheet',
  },
  {
    id: 'castle-malt-chateau-oat-nature',
    name: 'Malt Château Oat Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 78.5,
    moisturePercent: 7,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Certified-organic malted oats at 78.5 % extract, which is high for the grain, giving the silky body an organic stout or a robust British ale is bought oats for. Protein runs to 17 % and no diastatic power is published, so that figure is class-typical for oat malt.',
    tags: ['oat', 'adjunct', 'belgian', 'organic', 'body', 'creamy'],
    url: 'https://www.castlemalting.com/MaltInfo/?MALT-CHATEAU-OAT-NATURE-English',
    provenance: 'representative',
  },
  {
    id: 'castle-malt-chateau-rye-nature',
    name: 'Malt Château Rye Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 6,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic malted rye at 81 % extract, peppery and bread-like, and allowed to 30 % of an organic rye ale. Its own page prints two color figures — 4-10 EBC in the prose against 3-8 in the analysis — and the analysis table is what is stored. No diastatic power is published, so that figure is class-typical for rye.',
    tags: ['rye', 'base', 'belgian', 'organic', 'spicy'],
    url: 'https://www.castlemalting.com/MaltInfo/?MALT-CHATEAU-RYE-NATURE-English',
    provenance: 'representative',
  },
  {
    id: 'castle-malt-chateau-spelt-nature',
    name: 'Malt Château Spelt Nature',
    maker: 'Castle Malting',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 79.5,
    moisturePercent: 6,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic spelt malt at 3-7 EBC and 79.5 % extract, nutty and lightly honeyed, for organic saisons and wheat beers at up to 15 %. Protein reaches 17 % and no diastatic figure is published, so that one is class-typical.',
    tags: ['spelt', 'base', 'belgian', 'organic', 'ancient-grain', 'saison'],
    url: 'https://www.castlemalting.com/MaltInfo/?MALT-CHATEAU-SPELT-NATURE-English',
    provenance: 'representative',
  },
  // The rest of the Dingemans range, off their own product pages. Two habits of
  // that site are worth stating once. Their analysis table repeats `min. 230 WK`
  // on nearly every malt, roasted and caramelised ones included, where zero is
  // what the process leaves — so the row is boilerplate rather than a figure,
  // and it is read as one. And every page prints a headline `Colour EBC`
  // alongside a guarantee band; the headline is what is stored, because it is
  // the number Dingemans put in the product's own name.
  {
    id: 'dingemans-aroma-100-md',
    name: 'Aroma 100 MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 38.2,
    colorEbc: 100,
    diastaticPowerLintner: 15,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A long germination and a hard kiln give this one a far louder malt aroma than its 100 EBC would suggest, and it firms up flavor stability and body along with the color. Dingemans allow a fifth of the grist. Their prose calls its enzyme activity low while the analysis repeats the 230 WK boilerplate printed on every malt they sell, so the diastatic figure here is class-typical rather than theirs.',
    tags: ['kilned', 'belgian', 'aromatic', 'malty', 'amber'],
    url: 'https://www.dingemansmout.be/malt/aroma-100-md',
    provenance: 'representative',
  },
  {
    id: 'dingemans-aroma-150-md',
    name: 'Aroma 150 MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darker of the two Aroma grades, and the one that brings real color as well as aroma: deep bready malt for a Flemish brown, a barleywine or a dark abbey ale at up to a fifth of the bill. As with its lighter sibling the published 230 WK is the same boilerplate the roasted malts carry, against prose calling the enzyme activity low, so the figure stored is class-typical.',
    tags: ['kilned', 'belgian', 'aromatic', 'malty', 'dark-ale'],
    url: 'https://www.dingemansmout.be/malt/aroma-150-md',
    provenance: 'representative',
  },
  {
    id: 'dingemans-cara-200-md',
    // The id carries Dingemans' own designation rather than either number, so it
    // cannot be misread against the legacy `dingemans-cara-45` / `-cara-120`
    // pair above, which are EBC where the names beside them are Lovibond.
    name: 'Cara 75 (Cara 200 MD)',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5.5,
    colorLovibond: 75.9,
    colorEbc: 200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest Dingemans caramel short of Special B, drum-caramelized and then dried hard so the sweetness concentrates into dark toffee and dried fruit. This is the rung between Cara 45 and Special B that a dubbel or a Belgian strong dark reaches for, and Belgian abbey brewers use a lot of it. Dingemans sell it as Cara 200 MD after its EBC; the American trade renumbers it Cara 75 after its Lovibond.',
    tags: ['crystal', 'caramel', 'belgian', 'dark-fruit', 'abbey', 'dubbel', 'cara-200-md'],
    url: 'https://www.dingemansmout.be/malt/cara-200-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-cara-md-pilsen',
    name: 'Cara MD Pilsen',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71,
    moisturePercent: 6,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest caramel Dingemans make: honey, light biscuit and a fuller mouthfeel with almost no color, which is exactly what a blonde or a golden strong wants when it needs body rather than depth. Foam holds better from a twentieth of the grist and Dingemans will take it to thirty per cent. Their headline color is 6 EBC and the analysis prints 6.5; the headline is what is stored.',
    tags: ['crystal', 'caramel', 'belgian', 'light', 'head-retention', 'blonde'],
    url: 'https://www.dingemansmout.be/malt/cara-md-pilsen',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-chocolate-rye-300-md',
    name: 'Chocolate Rye 300 MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 113.7,
    colorEbc: 300,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Their own Rye Malt MD taken into the roasting drum until it reads 300 EBC: the peppery rye is still there with chocolate, cocoa nib and coffee laid over it. A per cent will show and Dingemans stop at five. They publish color and moisture for it but no extract at all, so the yield here is class-typical, taken against the barley roasts beside it.',
    tags: ['roasted', 'rye', 'belgian', 'chocolate', 'coffee', 'spicy'],
    url: 'https://www.dingemansmout.be/malt/chocolate-rye-300-md',
    provenance: 'representative',
  },
  {
    id: 'dingemans-coffee-mroost-450-md',
    name: 'Coffee Mroost 450 MD (Light Chocolate)',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 170.3,
    colorEbc: 450,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A medium roast that lands squarely on freshly ground coffee with toasted nuts behind it, and at 450 EBC it darkens a stout or a dark abbey ale without taking it to black. The quick chill Dingemans give their roasts is what keeps the astringency down; five per cent is their ceiling.',
    tags: ['roasted', 'belgian', 'coffee', 'nutty', 'stout', 'porter'],
    url: 'https://www.dingemansmout.be/malt/coffee-mroost-450-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-dextrin-md',
    name: 'Dextrin MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A body malt rather than a color one: malted so that most of what it carries is unfermentable, which thickens the mouthfeel and holds the head while leaving the beer as pale as it found it. Dingemans built it for low- and no-alcohol beers, where thinness is the whole problem, and allow a quarter of the grist. They file it with their kilned malts rather than their caramels, and publish no diastatic figure for it.',
    tags: ['dextrin', 'carapils', 'belgian', 'body', 'head-retention', 'low-alcohol'],
    url: 'https://www.dingemansmout.be/malt/dextrin-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-karrewheat-77-md',
    name: 'Karrewheat 77 MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 29.5,
    colorEbc: 77,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Their Wheat Malt MD taken through the caramelizing drum instead of the kiln, which lays warm bread, biscuit and toffee over the usual wheat character and leaves a much fuller beer. It keeps no enzyme, so it wants a well-modified base malt beside it; a quarter of the grist is the ceiling. The headline 77 EBC that gives it its name sits below the middle of the 70-100 EBC band the analysis guarantees, and the headline is what is stored.',
    tags: ['crystal', 'caramel', 'wheat', 'belgian', 'toffee', 'head-retention'],
    url: 'https://www.dingemansmout.be/malt/karrewheat-77-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-maris-otter-md',
    name: 'Maris Otter MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Maris Otter malted on the continent: the nutty, biscuity depth British brewers buy the variety for, fuller than Dingemans own Pilsen MD, and enzymatic enough to carry a grist by itself. An odd thing to find from a Belgian maltster, and the obvious pick when a Belgian pale wants a British backbone under it.',
    tags: ['base', 'belgian', 'maris-otter', 'pale-ale', 'biscuit'],
    url: 'https://www.dingemansmout.be/malt/maris-otter-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-melanoidin-md',
    name: 'Melanoidin MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 26.9,
    colorEbc: 70,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted and kilned hard enough to force the Maillard chemistry in the kiln, so the beer arrives with the depth a long decoction would have built into it: darker color, a rounder malt aroma and better flavor stability. Dingemans allow a quarter of the grist, which is generous for a malt this loud.',
    tags: ['kilned', 'melanoidin', 'belgian', 'malty', 'red'],
    url: 'https://www.dingemansmout.be/malt/melanoidin-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-mroost-900-md',
    name: 'Mroost 900 MD (Chocolate)',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dingemans chocolate malt, roasted hard and chilled fast so the flavor lands on cocoa nib and coffee rather than on ash. It is the ordinary roast of the pair: the darker Mroost 1400 MD beside it is the de-bittered one, and this is what to reach for when a stout or a porter wants the roast to be heard rather than only seen.',
    tags: ['roasted', 'belgian', 'chocolate', 'coffee', 'stout', 'porter'],
    url: 'https://www.dingemansmout.be/malt/mroost-900-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-mroost-wheat-malt-30-md',
    name: 'Mroost Wheat Malt 30 MD (Roasted Wheat)',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 11.8,
    colorEbc: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat malt roasted slowly and gently, which at 30 EBC reads as hazelnut and toasted almond rather than as roast, with a noticeably fuller body behind it. It keeps no enzyme, so a well-modified base malt has to do the converting; a fifth of the grist is the ceiling. Dingemans file it with their roasted malts, though at this color it behaves like the toasted one it is typed as here.',
    tags: ['wheat', 'toasted', 'belgian', 'nutty', 'body'],
    url: 'https://www.dingemansmout.be/malt/mroost-wheat-malt-30-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-oat-malt-md',
    name: 'Oat Malt MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 60,
    moisturePercent: 7,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted English oats with their big husks left on, which is why the published extract stops at sixty per cent and also why the bed still runs off. Creamy and porridge-like, it thickens the body and softens a hazy IPA or a stout at up to a quarter of the grist, though the wort is viscous and slower than wheat. Dingemans publish no diastatic power, so that figure is class-typical for oat malt.',
    tags: ['oat', 'adjunct', 'belgian', 'creamy', 'body', 'hull-on'],
    url: 'https://www.dingemansmout.be/malt/oat-malt-md',
    provenance: 'representative',
  },
  {
    id: 'dingemans-pealed-roasted-barley-md',
    name: 'Pealed Roasted Barley MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 453.3,
    colorEbc: 1200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley pearled before it goes in the drum, so the chaff that carries most of the harshness is off the grain before it ever meets the heat. What is left at 1200 EBC is coffee, toffee and a dry, faintly smoky finish. Dingemans cap it at five per cent, and in a dry stout that is already plenty. Their own spelling of the name is Pealed, and it is kept as they sell it.',
    tags: ['roasted', 'roasted-barley', 'belgian', 'coffee', 'stout', 'dry'],
    url: 'https://www.dingemansmout.be/malt/pealed-roasted-barley-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-pilsen-md-belgium-origin',
    name: 'Pilsen MD Belgium Origin',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The same pilsner malt as their standard Pilsen MD, restricted to two-row Belgian summer barley rather than European — a separate SKU for a brewer who wants the grain traced to the country the beer names. Fully and evenly modified, low in protein, at home in a single-infusion mash and happy as the whole bill.',
    tags: ['base', 'belgian', 'pilsner', 'lager', 'single-origin'],
    url: 'https://www.dingemansmout.be/malt/pilsen-md-belgium-origin',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-rye-malt-md',
    name: 'Rye Malt MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 6,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'English rye kilned gently enough to keep it pale, and it brings the dry, peppery, herbal edge that makes a rye ale or a rye saison what it is. Rye has no husk and a viscous wort, so watch the lauter and think about rice hulls once it climbs past a fifth. Dingemans publish no inclusion rate for it at all, leaving that to the style, so the ceiling here is ours rather than theirs.',
    tags: ['rye', 'base', 'belgian', 'spicy', 'saison'],
    url: 'https://www.dingemansmout.be/malt/rye-malt-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-toasted-rye-40-md',
    name: 'Toasted Rye 40 MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 15.5,
    colorEbc: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Their Rye Malt MD taken lightly through the roasting drum: the rye spice survives intact with warm bread and biscuit laid over it, and at 40 EBC there is barely any roast bitterness. Dingemans allow up to thirty per cent, far more than a roast normally gets, which is the clearest measure of how gentle it is. They publish no extract for it, so the yield here is class-typical.',
    tags: ['rye', 'toasted', 'belgian', 'spicy', 'bready'],
    url: 'https://www.dingemansmout.be/malt/toasted-rye-40-md',
    provenance: 'representative',
  },
  {
    id: 'dingemans-organic-biscuit-md',
    name: 'Organic Biscuit MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic twin of their Biscuit malt, and the lightest thing Dingemans put through the roasting drum: warm bread and digestive biscuit with none of the caramel sweetness a crystal would bring. It carries no enzyme of its own, so mash it against a base malt. The organic sheet allows it to thirty per cent, twice what the conventional grade is usually given.',
    tags: ['kilned', 'toasted', 'belgian', 'organic', 'biscuit'],
    url: 'https://www.dingemansmout.be/malt/organic-biscuit%C2%AE-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-organic-cara-md',
    name: 'Organic Cara MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 47.6,
    colorEbc: 125,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The one organic caramel Dingemans make, drum-caramelized on a rising temperature to a rich and frankly sweet caramel at 125 EBC — a step past their Cara 45 and short of Cara 75, which puts it exactly where an organic abbey beer wants it. Foam improves from a twentieth of the grist and a quarter is the ceiling.',
    tags: ['crystal', 'caramel', 'belgian', 'organic', 'abbey', 'dark-fruit'],
    url: 'https://www.dingemansmout.be/malt/organic-cara-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-organic-mroost-1400-md',
    name: 'Organic Mroost 1400 MD (De-bittered Black)',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 528.8,
    colorEbc: 1400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic de-bittered black: roasted to 1400 EBC and then chilled quickly, so it gives coffee, cappuccino and cocoa and very nearly black color without the scorched bite an ordinary black malt brings with it. The conventional Mroost 1400 MD is the same malt from non-organic barley, and five per cent is the ceiling on both.',
    tags: ['roasted', 'belgian', 'organic', 'debittered', 'black', 'coffee'],
    url: 'https://www.dingemansmout.be/malt/organic-mroost-1400-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-organic-munich-md',
    name: 'Organic Munich MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.5,
    moisturePercent: 4.5,
    colorLovibond: 6.1,
    colorEbc: 15,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 80,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic Munich at 15 EBC, kilned harder than their organic pilsner malt for a pronounced maltiness and an amber-orange cast. That kilning costs it enzyme, but not enough to matter: Dingemans still allow it to eighty per cent of the bill, which makes it a base malt as much as a color one.',
    tags: ['munich', 'kilned', 'belgian', 'organic', 'malty', 'amber'],
    url: 'https://www.dingemansmout.be/malt/organic-munich-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-organic-pale-ale-md',
    name: 'Organic Pale Ale MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 3.8,
    colorEbc: 9,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Fully modified organic pale ale malt, kilned longer and warmer than their organic pilsner for a golden cast and a rounder malt character. Protein and glucan are both low, so it clarifies easily and takes a single-infusion mash without complaint, and it will carry a whole organic grist on its own.',
    tags: ['base', 'belgian', 'organic', 'ale', 'pale-ale'],
    url: 'https://www.dingemansmout.be/malt/organic-pale-ale-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-organic-pilsen-md',
    name: 'Organic Pilsen MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The organic version of the Belgian reference pale base malt, made from two-row European organic summer barley. Evenly modified and low in protein, built for a single-infusion mash, and it will make up the whole grist of an organic pils, blonde or tripel with the same soft graininess the conventional grade has.',
    tags: ['base', 'belgian', 'organic', 'pilsner', 'lager'],
    url: 'https://www.dingemansmout.be/malt/organic-pilsen-md',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-organic-pilsen-md-belgium-origin',
    name: 'Organic Pilsen MD Belgium Origin',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic pilsner malt held to Belgian-grown two-row summer barley, sold beside the European-sourced organic grade for brewers who need the origin as well as the certification. It yields a point less extract and is friable a shade less readily than that sibling; otherwise it is the same malt and it will carry the whole bill.',
    tags: ['base', 'belgian', 'organic', 'pilsner', 'single-origin'],
    url: 'https://www.dingemansmout.be/malt/organic-pilsen-md-belgium-origin',
    provenance: 'spec-sheet',
  },
  {
    id: 'dingemans-organic-wheat-malt-md',
    name: 'Organic Wheat Malt MD',
    maker: 'Dingemans',
    origin: 'Belgium',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 6.5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Organic wheat malt at 81 % extract: the haze and the fine wheat flavor behind an organic witbier, and a real gain in head retention from only a small addition. Huskless like every wheat malt, so plan the lauter if it climbs toward the seventy per cent Dingemans allow.',
    tags: ['base', 'wheat', 'belgian', 'organic', 'witbier', 'head-retention'],
    url: 'https://www.dingemansmout.be/malt/organic-wheat-malt-md',
    provenance: 'spec-sheet',
  },
];
